// P13 native landing editor API over the L12 native_api helper. Every write
// carries the session CSRF token, one Idempotency-Key per intent (reused on a
// retry after a lost response) and the exact If-Match the server issued.
// Methods resolve with { data, etag, meta } or reject with a plain error
// { code, status, message, fields, current_version, reason }: a failed request
// never looks like success, and an unknown outcome is its own code.
import { apiRequest, versionTag } from '~/javascript/components/landing_pages/native_api'

export class LandingApiError extends Error {
  constructor(result) {
    const error = result.error || {}
    super(error.message || error.code || 'request_failed')
    this.status = result.status
    this.code = result.status === 0 ? 'outcome_unknown' : (error.code || 'request_failed')
    this.fields = error.fields || {}
    this.current_version = error.current_version ?? null
    this.reason = error.reason || null
    this.retryable = result.status === 0 || error.retryable === true
  }
}

export function sha256Hex(bytes) {
  return globalThis.crypto.subtle.digest('SHA-256', bytes).then((digest) => Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0')).join(''))
}

export function createLandingApi({ endpoints = {}, csrfToken = '', request = apiRequest } = {}) {
  const base = () => endpoints.workspace
  const media = () => endpoints.media

  async function call(url, { method = 'GET', body, version } = {}) {
    const result = await request(url, { method, body, csrfToken, etag: version ? versionTag(version) : undefined })
    if (!result.ok) throw new LandingApiError(result)
    return { data: result.data, etag: result.etag, meta: result.meta, status: result.status }
  }

  return {
    endpoints,
    createWorkspace: (body) => call(endpoints.create_workspace, { method: 'POST', body }),
    workspace: () => call(base()),
    updateWorkspace: (body, version) => call(base(), { method: 'PATCH', body, version }),
    templates: () => call(`${base()}/templates`),
    sites: () => call(`${base()}/sites`),
    createSite: (body) => call(`${base()}/sites`, { method: 'POST', body }),
    site: (siteId) => call(`${base()}/sites/${siteId}`),
    siteImpact: (siteId) => call(`${base()}/sites/${siteId}/impact`),
    // The owner's explicit native registry observation; resolves with the site impact.
    observeRegistry: (siteId) => call(`${base()}/sites/${siteId}/registry_observations`, { method: 'POST', body: {} }),
    domains: (siteId) => call(`${base()}/sites/${siteId}/domains`),
    claimDomain: (siteId, body) => call(`${base()}/sites/${siteId}/domains`, { method: 'POST', body }),
    verifyDomain: (siteId, domainId, groupVersion) => call(
      `${base()}/sites/${siteId}/domains/${domainId}/verify`,
      { method: 'POST', body: {}, version: groupVersion }
    ),
    promoteDomain: (siteId, domainId, groupVersion, impactDigest) => call(
      `${base()}/sites/${siteId}/domains/${domainId}/promote`,
      { method: 'POST', body: { impact_digest: impactDigest }, version: groupVersion }
    ),
    removeDomain: (siteId, domainId, groupVersion, impactDigest) => call(
      `${base()}/sites/${siteId}/domains/${domainId}/remove`,
      { method: 'POST', body: { impact_digest: impactDigest }, version: groupVersion }
    ),
    pages: (siteId) => call(`${base()}/pages${siteId ? `?site_id=${encodeURIComponent(siteId)}` : ''}`),
    createPage: (body) => call(`${base()}/pages`, { method: 'POST', body }),
    page: (pageId) => call(`${base()}/pages/${pageId}`),
    saveDraft: (pageId, body, version) => call(`${base()}/pages/${pageId}`, { method: 'PATCH', body, version }),
    changePath: (pageId, path, version) => call(`${base()}/pages/${pageId}`, { method: 'PATCH', body: { path }, version }),
    duplicatePage: (pageId, body, version) => call(`${base()}/pages/${pageId}/duplicate`, { method: 'POST', body, version }),
    archivePage: (pageId, impactDigest, version) => call(
      `${base()}/pages/${pageId}/archive`,
      { method: 'POST', body: { impact_digest: impactDigest }, version }
    ),
    checkpoint: (pageId, version) => call(`${base()}/pages/${pageId}/checkpoints`, { method: 'POST', body: {}, version }),
    revisions: (pageId) => call(`${base()}/pages/${pageId}/revisions`),
    impact: (pageId) => call(`${base()}/pages/${pageId}/impact`),
    readiness: (pageId, revisionId) => call(`${base()}/pages/${pageId}/readiness${revisionId ? `?revision_id=${encodeURIComponent(revisionId)}` : ''}`),
    refreshReadiness: (pageId, revisionId) => call(
      `${base()}/pages/${pageId}/readiness`,
      { method: 'POST', body: { revision_id: revisionId } }
    ),
    publish: (pageId, body, version) => call(`${base()}/pages/${pageId}/publications`, { method: 'POST', body, version }),
    rollback: (pageId, body, version) => call(`${base()}/pages/${pageId}/rollback`, { method: 'POST', body, version }),
    operation: (operationId) => call(`${media()}/operations/${operationId}`),
    preview: (pageId, draftVersion, version) => call(
      `${media()}/pages/${pageId}/previews`,
      { method: 'POST', body: { draft_version: draftVersion }, version }
    ),
    // One keyset page of the page's completed assets (M09 listing).
    assets: (pageId, { limit, cursor } = {}) => {
      const query = new URLSearchParams()
      if (limit) query.set('limit', String(limit))
      if (cursor) query.set('cursor', cursor)
      const suffix = query.toString() ? `?${query}` : ''
      return call(`${media()}/pages/${pageId}/assets${suffix}`)
    },
    generate: (pageId, body, version) => call(`${media()}/pages/${pageId}/generations`, { method: 'POST', body, version }),
    // Two-phase upload: declare the SHA-256, PUT the bytes to the signed
    // upload URL, then complete against the same declared checksum.
    async uploadAsset(pageId, file, version) {
      const bytes = await file.arrayBuffer()
      const checksum = await sha256Hex(bytes)
      const created = await call(`${media()}/pages/${pageId}/assets`, {
        method: 'POST',
        version,
        body: { filename: file.name, content_type: file.type, byte_size: file.size, checksum, checksum_algorithm: 'sha256' }
      })
      const upload = created.data
      let put
      try {
        put = await fetch(upload.upload_url, {
          method: upload.upload_method || 'PUT',
          headers: { 'Content-Type': file.type, ...(upload.required_headers || {}) },
          body: bytes
        })
      } catch (error) {
        throw new LandingApiError({ status: 0, error: { code: 'upload_failed' } })
      }
      if (!put.ok) throw new LandingApiError({ status: put.status, error: { code: 'upload_failed' } })
      return call(
        `${media()}/pages/${pageId}/assets/${upload.asset_id}/complete`,
        { method: 'POST', body: { checksum }, version }
      )
    }
  }
}

// Every completed asset of a page, following next_cursor (bounded so a
// misbehaving cursor can never loop forever).
export async function listAllAssets(api, pageId, { limit = 50, maxPages = 20 } = {}) {
  const assets = []
  let cursor = null
  for (let index = 0; index < maxPages; index += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await api.assets(pageId, { limit, cursor })
    assets.push(...(result.data || []))
    cursor = result.meta?.next_cursor || null
    if (!cursor) break
  }
  return assets
}

// Polls an Operation until it settles; never reports success before the
// server does. Resolves with the last representation read.
export async function waitForOperation(api, operationId, { intervalMs = 1500, attempts = 120, onUpdate } = {}) {
  let last = null
  for (let index = 0; index < attempts; index += 1) {
    // eslint-disable-next-line no-await-in-loop
    const read = await api.operation(operationId)
    last = read.data
    if (onUpdate) onUpdate(last)
    if (['succeeded', 'failed'].includes(last.state)) return last
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => { setTimeout(resolve, intervalMs) })
  }
  return last
}
